import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Panel from '../ui/Panel'
import PlayerCard from '../ui/PlayerCard'
import Avatar from '../ui/Avatar'
import WatchlistFeed from './WatchlistFeed'
import './favourites.css'

/*
 * THE SCOUT'S WATCHLIST
 *
 * Left: the players this manager follows — rank, hover card, position, country,
 * points, each row opening the full player page.
 * Right: WatchlistFeed, one merged stream of what those players have been doing.
 *
 * The endpoint returns an empty array for accounts that have not starred anyone
 * yet, so the empty state is the state most managers meet first: it has to say
 * what a watchlist is for and where players are added.
 */

export default function FavouritePlayers() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = () => {
    setFailed(false)
    axios.get(process.env.REACT_APP_API_SERVER + 'manager/dashboard/favouriteplayers/' + sessionStorage.getItem('SportID') + '/' + sessionStorage.getItem('userToken'), {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
      }
    })
      .then(res => {
        // Same response shape as before; the old .slice(0, 5) is gone because a
        // dashboard teaser cap has no place on the page dedicated to the list.
        setData(Array.isArray(res.data) ? res.data : [])
        setIsLoading(false)
      }).catch(err => {
        setFailed(true)
        setIsLoading(false)
      })
  }

  const plural = (n, one, many) => (n === 1 ? one : many)

  const pointsOf = (item) => {
    const n = Number(item.v)
    return Number.isFinite(n) ? n : 0
  }

  const dataList = data.map((item, index) => {
    return (
      <Link className="fav-row" to={`/user/${item.UUID}`} key={item.UUID || index}>
        <span className="fav-rank num">{String(index + 1).padStart(2, '0')}</span>

        <PlayerCard
          uuid={item.UUID}
          name={item.Name}
          position={item.Position}
          points={item.v}
        >
          {/* 36px is the width of the avatar column in .fav-row — the grid is untouched */}
          <Avatar uuid={item.UUID} name={item.Name} size={36} />
        </PlayerCard>

        <span className="fav-who">
          <span className="fav-name">{item.Name}</span>
          <span className="fav-pos">{item.Position ? item.Position : 'Position not set'}</span>
        </span>

        <span className="fav-country">{item.Country ? item.Country : '—'}</span>

        <span className="fav-points">
          <b className="num">{pointsOf(item)}</b>
          <span className="mono">pts</span>
        </span>
      </Link>
    )
  })

  return (
    <div className="fav-canvas">
      <div className="grid grid-2">

        <Panel
          title="Watchlist"
          subtitle={!isLoading && data.length > 0
            ? `${data.length} ${plural(data.length, 'player', 'players')} you follow`
            : 'Players you follow'}
        >
          {isLoading && (
            <div className="wf-skeleton">
              <div className="skeleton wf-sk"></div>
              <div className="skeleton wf-sk"></div>
              <div className="skeleton wf-sk wf-sk-short"></div>
            </div>
          )}

          {!isLoading && failed && (
            <div className="fav-alert" role="alert">
              <b>Your watchlist could not be loaded.</b>
              <span>The server did not answer. Nothing has been lost — try again.</span>
              <button type="button" className="btn-ui" onClick={() => { setIsLoading(true); loadData() }}>
                Try again
              </button>
            </div>
          )}

          {!isLoading && !failed && data.length === 0 && (
            <div className="fav-blank">
              <span className="fav-blank-mark" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M12 20.5S3.5 15.2 3.5 9.4A4.4 4.4 0 0 1 12 7.4a4.4 4.4 0 0 1 8.5 2c0 5.8-8.5 11.1-8.5 11.1z" />
                </svg>
              </span>
              <h3 className="fav-blank-title">Your watchlist is empty</h3>
              <p className="fav-blank-text">
                A watchlist is the short list of players you are actually tracking. Star a player
                and they stay here — with their position, country and points — while everything they
                post and every challenge they enter lands in the activity feed beside this panel.
              </p>
              <Link className="btn-ui btn-accent" to="/playerslist">Browse Players List</Link>
              <p className="fav-blank-hint">Open a player from the list and add them to your favourites.</p>
            </div>
          )}

          {!isLoading && !failed && data.length > 0 && (
            <div className="fav-list scroll-body fav-scroll">
              <div className="fav-head">
                <span className="mono"></span>
                <span className="mono"></span>
                <span className="mono">Player &amp; position</span>
                <span className="mono">Country</span>
                <span className="mono fav-head-right">Points</span>
              </div>
              {dataList}
            </div>
          )}
        </Panel>

        <WatchlistFeed players={data} loading={isLoading} />

      </div>
    </div>
  )
}
